/**
 * Numbers in report text, read the same way as the backend
 * (backend/src/services/numberText.util.js, R1b and R1d; keep the two in step).
 *
 * parseFloat stops at a comma, a space or an apostrophe, so a WBC of "6,100" was read as
 * 6, "6 100" or "6'100" as 6, and "6.100,5" as 6.1. The leading number of a text is read
 * only when its form is unambiguous:
 * - plain: 95, -5, 0.5, .5, 1e5 (as parseFloat reads it)
 * - Western grouping: 11,000 -> 11000; 1,234.5 -> 1234.5 (groups of 3)
 * - Indian grouping: 4,10,000 -> 410000 (groups of 2, then a final group of 3)
 * and the first group of a grouping does not start with 0 ("0,500" is likely a decimal
 * comma). Digit groups joined any other way (a space, a thin or no-break space, an
 * apostrophe, ", ", a decimal comma "5,5", dots and commas mixed "6.100,5", two dots)
 * are ambiguous: no number, never a guess. Text that does not start with a number
 * ("<6,100", "Positive") has no number; what follows the number ("7.5 H",
 * "95, fasting", "120/80") is ignored. The text itself is never changed.
 *
 * Signs (R1f): "+", and "-" written as the ASCII hyphen-minus or as the Unicode minus
 * signs a PDF carries (U+2212 minus, U+FE63 small hyphen-minus, U+FF0D fullwidth
 * hyphen-minus). Any other dash or sign right before the digits (en or em dash, hyphen,
 * fullwidth plus...) cannot be read safely: no number. Only the sign position is read
 * this way.
 */

export interface NumberReading {
  /** The number, or null when there is none */
  number: number | null;
  /** The digits were grouped in a way that cannot be read safely */
  ambiguous: boolean;
}

// What may sit between two digit groups of one number: commas, dots, spaces (also
// no-break, thin and other Unicode spaces) and apostrophes. Only a single comma or a
// single dot can be part of an unambiguous number.
const SEPARATOR = /[,.'\u2018\u2019\u0060\u00B4\u2032\s\u00A0\u2000-\u200B\u202F\u205F\u3000]/;
const isDigit = (ch: string): boolean => ch >= '0' && ch <= '9';
// An exponent straight after a plain number: "1e5", "2.5E-3"
const EXPONENT = /^[eE][+-]?\d+/;
// Longer than any real number, so a pathological text is never scanned for long
const MAX_NUMBER_TEXT = 64;
// Read as "-" in the sign position (R1f)
const MINUS_SIGNS = new Set(['-', '\u2212', '\uFE63', '\uFF0D']);
// Any other dash or sign there: hyphen, non-breaking hyphen, figure dash, en and em dash,
// horizontal bar, soft hyphen, super/subscript and heavy minus and plus, small and
// fullwidth plus
const UNREADABLE_SIGNS = new Set([
  '\u2010', '\u2011', '\u2012', '\u2013', '\u2014', '\u2015', '\u00AD',
  '\u207B', '\u208B', '\u2796', '\u207A', '\u208A', '\u2795', '\uFE62', '\uFF0B',
]);

interface LeadingNumber {
  sign: string;
  groups: string[];
  separators: string[];
  end: number;
  /** The digits follow a dash or sign that is not "+" or a minus (R1f) */
  unreadableSign: boolean;
}

/**
 * The number at the start of `value`, as digit groups and what separates them. A
 * separator run belongs to the number only when a digit follows it ("95, fasting" ends
 * at 95). A leading-dot decimal (".5") is one group ".5". null when no number starts there.
 */
function leadingNumberOf(value: string): LeadingNumber | null {
  let i = 0;
  let sign = '';
  let unreadableSign = false;
  const first = value.charAt(0);
  if (first === '+') sign = '+';
  else if (MINUS_SIGNS.has(first)) sign = '-';
  else if (UNREADABLE_SIGNS.has(first)) unreadableSign = true;
  if (sign || unreadableSign) i = 1;
  const startsWithDot = value.charAt(i) === '.' && isDigit(value.charAt(i + 1));
  if (!isDigit(value.charAt(i)) && !startsWithDot) return null;
  const groups: string[] = [];
  const separators: string[] = [];
  let group = startsWithDot ? '.' : '';
  if (startsWithDot) i += 1;
  const limit = Math.min(value.length, MAX_NUMBER_TEXT);
  while (i < limit) {
    if (isDigit(value.charAt(i))) {
      group += value.charAt(i);
      i += 1;
      continue;
    }
    // A run of separators, part of the number only if a digit follows it
    let j = i;
    while (j < limit && SEPARATOR.test(value.charAt(j))) j += 1;
    if (j === i || j >= limit || !isDigit(value.charAt(j))) break;
    groups.push(group);
    separators.push(value.slice(i, j));
    group = '';
    i = j;
  }
  groups.push(group);
  return { sign, groups, separators, end: i, unreadableSign };
}

// Western: 1-3 digits, then groups of 3. Indian: 1-2 digits, groups of 2, a final group of
// 3. Either way the first group does not start with 0. (Checked on the groups, not by one
// regex: no repeated groups, so no catastrophic backtracking.)
const isWesternGrouping = (groups: string[]): boolean =>
  groups[0].length <= 3 && groups.slice(1).every((group) => group.length === 3);
const isIndianGrouping = (groups: string[]): boolean =>
  groups.length > 2 &&
  groups[0].length <= 2 &&
  groups.slice(1, -1).every((group) => group.length === 2) &&
  groups.slice(-1)[0].length === 3;
const isGrouping = (groups: string[]): boolean =>
  groups.length > 1 && groups[0].charAt(0) !== '0' && (isWesternGrouping(groups) || isIndianGrouping(groups));

/** The value of a leading number, or ambiguous. */
function valueOf({ sign, groups, separators }: LeadingNumber, rest: string): NumberReading {
  // Plain: one group, or two joined by a single dot
  if (separators.length === 0) {
    const exponent = !groups[0].startsWith('.') ? EXPONENT.exec(rest) : null;
    return { number: Number(sign + groups[0] + (exponent ? exponent[0] : '')), ambiguous: false };
  }
  if (separators.length === 1 && separators[0] === '.' && !groups[0].startsWith('.')) {
    const exponent = EXPONENT.exec(rest);
    return { number: Number(`${sign}${groups[0]}.${groups[1]}${exponent ? exponent[0] : ''}`), ambiguous: false };
  }
  // Comma grouping, with decimals after a final single dot ("1,234.5")
  const last = separators.length - 1;
  const decimals = separators.slice(-1)[0] === '.' ? groups.slice(-1)[0] : null;
  const commaSeparators = decimals === null ? separators : separators.slice(0, last);
  const commaGroups = decimals === null ? groups : groups.slice(0, last + 1);
  if (commaSeparators.every((separator) => separator === ',') && !commaGroups[0].startsWith('.') && isGrouping(commaGroups)) {
    return { number: Number(`${sign}${commaGroups.join('')}${decimals === null ? '' : `.${decimals}`}`), ambiguous: false };
  }
  return { number: null, ambiguous: true };
}

/** Reads a limit or value as the AI (or the nurse) wrote it. */
export function readNumber(text: unknown): NumberReading {
  if (text === null || text === undefined) return { number: null, ambiguous: false };
  const value = String(text).trim();
  const lead = leadingNumberOf(value);
  if (!lead) return { number: null, ambiguous: false };
  if (lead.unreadableSign) return { number: null, ambiguous: true };
  const read = valueOf(lead, value.slice(lead.end));
  if (read.ambiguous) return read;
  return { number: read.number !== null && Number.isFinite(read.number) ? read.number : null, ambiguous: false };
}
